import React, { useEffect, useState } from 'react';
import { Modal, PropertyCombobox } from '../ui';
import {
  createApplicationAsSelf,
  createApplicationOnBehalfOf,
  listHoaProperties,
  listPropertiesForCurrentUser,
  uploadACCAttachments,
} from '../../services/accApplicationsService';

const inputClass =
  'w-full px-4 py-3 bg-white border border-amber-100/40 rounded-xl text-primary-dark placeholder-slate-400 text-base md:text-sm font-medium outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/10 transition-all';
const labelClass = 'block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2';

const MaterialIcon = ({ name, size = 20, className = '' }) => (
  <span className={`material-symbols-outlined ${className}`} style={{ fontSize: `${size}px` }}>
    {name}
  </span>
);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const formatPhone = (raw) => {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const ACCApplicationCreateModal = ({ isOpen, onClose, onCreated, currentUser }) => {
  const [mode, setMode] = useState('self');
  const [projectTitle, setProjectTitle] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitterPhone, setSubmitterPhone] = useState('');
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  const canSubmitOnBehalf = ['admin', 'management'].includes(currentUser?.role);
  const hoaId = currentUser?.hoa_id;

  useEffect(() => {
    if (!isOpen) return;
    setMode('self');
    setProjectTitle('');
    setProjectDescription('');
    setSubmitterName('');
    setSubmitterEmail('');
    setSubmitterPhone('');
    setPropertyOptions([]);
    setSelectedPropertyId('');
    setError('');
    setConfirmDiscard(false);
    setIsSubmitting(false);
    setPendingFiles([]);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !hoaId) return undefined;
    let mounted = true;

    const loadProperties = async () => {
      setPropertiesLoading(true);
      setSelectedPropertyId('');
      const result =
        mode === 'self'
          ? await listPropertiesForCurrentUser(hoaId)
          : await listHoaProperties(hoaId);

      if (!mounted) return;
      setPropertiesLoading(false);
      if (result.error) {
        setPropertyOptions([]);
        setError(result.error.message || 'Properties could not be loaded.');
        return;
      }

      const properties = result.data || [];
      setPropertyOptions(properties);
      if (properties.length === 1) setSelectedPropertyId(properties[0].id);
    };

    loadProperties().then(null, (loadError) => {
      if (!mounted) return;
      setPropertiesLoading(false);
      setPropertyOptions([]);
      setError(loadError.message || 'Properties could not be loaded.');
    });

    return () => {
      mounted = false;
    };
  }, [hoaId, isOpen, mode]);

  const hasSubstantialContent =
    projectTitle.trim().length > 0 ||
    projectDescription.trim().length > 0 ||
    submitterName.trim().length > 0 ||
    submitterEmail.trim().length > 0 ||
    selectedPropertyId.length > 0;

  const isValid =
    projectTitle.trim().length > 0 &&
    projectTitle.trim().length <= 200 &&
    projectDescription.trim().length >= 20 &&
    projectDescription.trim().length <= 5000 &&
    selectedPropertyId.length > 0 &&
    (mode === 'self' || (submitterName.trim().length > 0 && isValidEmail(submitterEmail.trim())));

  const handleClose = () => {
    if (confirmDiscard) {
      setConfirmDiscard(false);
      return;
    }
    if (isSubmitting) return;
    if (hasSubstantialContent) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isValid || isSubmitting) return;
    if (!hoaId) {
      setError('Community context is not ready. Please refresh or sign in again.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    const selectedProperty = propertyOptions.find((property) => property.id === selectedPropertyId);
    if (!selectedProperty) {
      setIsSubmitting(false);
      setError('Select the property for this ACC application.');
      return;
    }

    try {
      const result =
        mode === 'self'
          ? await createApplicationAsSelf({
              hoaId,
              projectTitle,
              projectDescription,
              propertyId: selectedProperty.id,
              propertyAddressSnapshot: selectedProperty.situs_address,
              actorUserId: currentUser?.id,
            })
          : await createApplicationOnBehalfOf({
              hoaId,
              projectTitle,
              projectDescription,
              submitterName,
              submitterEmail,
              submitterPhone,
              propertyId: selectedProperty.id,
              propertyAddressSnapshot: selectedProperty.situs_address,
              actorUserId: currentUser?.id,
            });

      if (result.error) {
        setError(result.error.message || 'Unable to submit application.');
        return;
      }

      if (pendingFiles.length > 0 && result.data?.id) {
        const up = await uploadACCAttachments({
          applicationId: result.data.id, hoaId, files: pendingFiles,
        });
        if (up.error) {
          setError(`Application submitted, but an attachment failed: ${up.error.message}`);
          // Still proceed — the application exists. Refresh + close below.
        }
      }

      Promise.resolve(onCreated?.(result.data)).then(null, (createdError) => {
        console.error('ACC application refresh error:', createdError);
      });
      onClose();
    } catch (submitError) {
      console.error('ACC application submit error:', submitError);
      setError(submitError.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="w-full max-w-3xl"
      mobileMode="fullscreen"
    >
      <form
        data-testid="acc-create-modal"
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
        className="relative w-full max-h-[90vh] max-md:h-full max-md:max-h-none overflow-y-auto bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30"
      >
        {/* Inline discard confirmation — renders INSIDE the form, no second modal */}
        {confirmDiscard && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95 rounded-2xl">
            <div className="p-8 max-w-sm w-full text-center">
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MaterialIcon name="warning" className="text-amber-500" size={24} />
              </div>
              <h3 className="font-bold text-primary-dark text-lg mb-2">
                Discard this application?
              </h3>
              <p className="text-slate-500 text-sm font-medium mb-6">
                Typed application details will be lost.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmDiscard(false)}
                  className="flex-1 border border-slate-200 text-slate-700 font-semibold rounded-full px-4 py-2.5 text-sm hover:bg-section-bg transition-colors"
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDiscard(false);
                    onClose();
                  }}
                  className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-full px-4 py-2.5 text-sm transition-colors"
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="sticky top-0 z-20 bg-white flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-amber-100/30">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 bg-section-bg rounded-xl flex items-center justify-center">
                <MaterialIcon name="architecture" className="text-secondary" size={24} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-primary-dark">
                New ACC Application
              </h2>
            </div>
            <p className="text-sm font-medium text-slate-500">
              Preliminary guidance is not formal board approval. CC&Rs provide a 30-day response
              window.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-11 h-11 rounded-full border border-amber-100/40 text-primary-dark hover:bg-section-bg transition-all flex items-center justify-center"
          >
            <MaterialIcon name="close" size={22} />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-6">
          {canSubmitOnBehalf && (
            <div className="inline-flex max-sm:w-full max-sm:flex-col rounded-[2rem] bg-section-bg p-1">
              {[
                ['self', 'Submit for myself'],
                ['other', 'Submit on behalf'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setMode(value);
                    setError('');
                  }}
                  data-testid={`acc-create-mode-${value}`}
                  className={`px-5 py-3 rounded-full text-sm font-bold transition-all ${mode === value ? 'bg-secondary text-white' : 'text-primary-dark hover:bg-white'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {mode === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Submitter name</label>
                <input
                  data-testid="acc-submitter-name"
                  className={inputClass}
                  value={submitterName}
                  onChange={(e) => setSubmitterName(e.target.value)}
                  maxLength={200}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Submitter email</label>
                <input
                  data-testid="acc-submitter-email"
                  className={inputClass}
                  type="email"
                  value={submitterEmail}
                  onChange={(e) => setSubmitterEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Submitter phone</label>
                <input
                  data-testid="acc-submitter-phone"
                  className={inputClass}
                  value={submitterPhone}
                  onChange={(e) => setSubmitterPhone(formatPhone(e.target.value))}
                  placeholder="(406) 555-1234"
                />
              </div>
            </div>
          )}

          <div>
            <label className={labelClass}>Property</label>
            <PropertyCombobox
              testId="acc-property-select"
              properties={propertyOptions}
              value={selectedPropertyId}
              onChange={setSelectedPropertyId}
              disabled={propertiesLoading}
            />
            {propertiesLoading && (
              <p className="text-xs font-medium text-slate-500 mt-2">Loading properties…</p>
            )}
            {!propertiesLoading && propertyOptions.length === 0 && (
              <p className="text-xs font-bold text-rose-700 mt-2">
                No active property is available for this application. Contact an administrator.
              </p>
            )}
            {mode === 'self' && propertyOptions.length > 1 && (
              <p className="text-xs font-medium text-slate-500 mt-2">
                Choose the property where the proposed work will occur.
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>Project title</label>
            <input
              data-testid="acc-project-title"
              className={inputClass}
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              maxLength={200}
              required
            />
          </div>

          <div>
            <label className={labelClass}>Project description</label>
            <textarea
              data-testid="acc-project-description"
              className={`${inputClass} min-h-40 resize-y`}
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              minLength={20}
              maxLength={5000}
              required
            />
            <div className="mt-2 text-xs font-medium text-slate-500">
              {projectDescription.trim().length}/5000 characters
            </div>
          </div>

          <div>
            <label className={labelClass}>Attachments (optional)</label>
            <input
              data-testid="acc-attachments-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.tiff,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              onChange={(e) => setPendingFiles(Array.from(e.target.files || []))}
              className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-secondary file:px-5 file:py-2.5 file:text-white file:font-bold hover:file:brightness-110 file:cursor-pointer"
            />
            <p className="text-[11px] font-medium text-slate-500 mt-2">Up to 25 MB total · PDF, images, and Office documents</p>
            {pendingFiles.length > 0 && (
              <ul className="mt-2 space-y-1">
                {pendingFiles.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="text-xs font-medium text-slate-500">{f.name} ({Math.ceil(f.size/1024)} KB)</li>
                ))}
              </ul>
            )}
          </div>

          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-800">
              {error}
            </div>
          )}
        </div>

        {!isValid && !isSubmitting && (
          <div className="px-5 sm:px-6 pb-2">
            <p className="text-xs font-bold text-slate-500">
              {projectTitle.trim().length === 0 && 'Add a project title to continue. '}
              {projectDescription.trim().length < 20 && 'Description must be at least 20 characters. '}
              {selectedPropertyId.length === 0 && 'Select the affected property. '}
              {mode === 'other' && submitterName.trim().length === 0 && 'Enter the submitter name. '}
              {mode === 'other' && !isValidEmail(submitterEmail.trim()) && 'Enter a valid submitter email (e.g. name@example.com). '}
            </p>
          </div>
        )}

        <div className="mobile-sticky-actions bg-white flex flex-col-reverse sm:flex-row justify-end gap-3 p-5 sm:p-6 border-t border-amber-100/30">
          <button
            type="button"
            onClick={handleClose}
            className="w-full sm:w-auto border border-secondary text-secondary px-8 py-3.5 rounded-full font-bold hover:bg-secondary hover:text-white transition-all"
          >
            Cancel
          </button>
          <button
            data-testid="acc-submit-application"
            type="submit"
            disabled={!isValid || isSubmitting}
            className="w-full sm:w-auto bg-secondary text-white px-8 py-3.5 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ACCApplicationCreateModal;
